/**
 * Reads the `exp` claim out of a JWT without verifying it.
 *
 * Verification is the receiving service's job. The client only needs to know when to go and ask for
 * a fresh token, and a tampered `exp` would at worst make it refresh at the wrong moment — which the
 * verifying service would reject anyway. So this deliberately does no signature checking, and the
 * result must never be used to decide whether a token is trustworthy.
 *
 * Returns null for anything it cannot read, so callers can treat "no usable expiry" as one case.
 *
 * @returns expiry as epoch milliseconds (JWT `exp` is in seconds), or null
 */
export const getJwtExpiryMs = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    // JWTs use base64url; atob expects standard base64, and tolerates missing padding poorly.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );

    // Safe without a UTF-8 decode: JWT payloads are JSON serialised with ASCII escaping, so any
    // non-ASCII claim value arrives as \uXXXX rather than raw multi-byte characters.
    const { exp } = JSON.parse(atob(padded));

    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    // Malformed token, non-JSON payload, bad base64 — all mean "no usable expiry".
    return null;
  }
};
